import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "error"]
    static values = {
        loadingPlaceholder: { type: String, default: "Calculating position..." },
        errorPlaceholder: { type: String, default: "Could not calculate position." },
        unsupportedPlaceholder: {
            type: String,
            default: "Geolocation is not supported by this browser.",
        },

        alertOnPrompt: { type: Boolean, default: true },

        format: { type: String, default: "comma" }, // "comma" | "comma_space"
        precision: { type: Number, default: 6 },

        enableHighAccuracy: { type: Boolean, default: true },
        timeout: { type: Number, default: 10000 },
        maximumAge: { type: Number, default: 60000 },

        // validation
        validate: { type: Boolean, default: true },
        invalidMessage: {
            type: String,
            default: "Please enter coordinates like: 52.5200, 13.4050",
        },
    }

    connect() {
        // validate on load if field already has a value
        this.validateIfEnabled()
    }

    validateIfEnabled() {
        if (!this.validateValue || !this.hasInputTarget) return
        this.validateInput()
    }

    // call this via blur/change
    validateInput() {
        if (!this.hasInputTarget) return

        const input = this.inputTarget
        const value = (input.value || "").trim()

        // empty is okay (leave required-ness to Rails/HTML)
        if (value === "") {
            this.setValid()
            return true
        }

        const parsed = this.parseCoords(value)
        if (!parsed) {
            this.setInvalid(this.invalidMessageValue)
            return false
        }

        const { lat, lon } = parsed
        const inRange = lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180

        if (!inRange) {
            this.setInvalid("Latitude must be -90..90 and longitude -180..180.")
            return false
        }

        this.setValid()
        return true
    }

    setInvalid(message) {
        this.inputTarget.setCustomValidity(message)
        this.inputTarget.classList.add("border-red-500")
        this.inputTarget.classList.remove("border-green-500")

        if (this.hasErrorTarget) {
            this.errorTarget.textContent = message
            this.errorTarget.classList.remove("hidden")
        }
    }

    setValid() {
        this.inputTarget.setCustomValidity("")
        this.inputTarget.classList.remove("border-red-500")
        this.inputTarget.classList.add("border-green-500")

        if (this.hasErrorTarget) {
            this.errorTarget.textContent = ""
            this.errorTarget.classList.add("hidden")
        }
    }

    parseCoords(value) {
        // Accept:
        //  - "52.52,13.405"
        //  - "52.52, 13.405"
        //  - "52.52 13.405"
        //  - "52.52;13.405"
        //
        // Optional leading +/-, decimals, and whitespace.
        const m = value.match(
            /^\s*([+-]?\d+(?:\.\d+)?)\s*[,;\s]\s*([+-]?\d+(?:\.\d+)?)\s*$/,
        )
        if (!m) return null

        const lat = Number(m[1])
        const lon = Number(m[2])
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null

        return { lat, lon }
    }

    fill(event) {
        event?.preventDefault()
        if (!this.hasInputTarget) return

        const input = this.inputTarget
        input.value = ""
        input.placeholder = this.loadingPlaceholderValue

        const requestPosition = () => {
            if (!("geolocation" in navigator)) {
                input.placeholder = this.unsupportedPlaceholderValue
                return
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    input.placeholder = ""
                    const { latitude, longitude } = position.coords
                    input.value = this.formatCoords(latitude, longitude)

                    input.dispatchEvent(new Event("input", { bubbles: true }))
                    input.dispatchEvent(new Event("change", { bubbles: true }))

                    this.validateIfEnabled()
                },
                (error) => {
                    input.placeholder = this.errorPlaceholderValue
                    console.warn(`Geolocation ERROR(${error.code}): ${error.message}`)
                },
                {
                    enableHighAccuracy: this.enableHighAccuracyValue,
                    timeout: this.timeoutValue,
                    maximumAge: this.maximumAgeValue,
                },
            )
        }

        const permissions = navigator.permissions
        if (permissions?.query) {
            permissions
                .query({ name: "geolocation" })
                .then((status) => {
                    const skipAlert = window.railsEnv === "test"
                    if (this.alertOnPromptValue && status.state === "prompt" && !skipAlert) {
                        alert(
                            "We are going to request your location to fill in the GPS location field. Please allow access to your location via the browser, otherwise the form can not be submitted.",
                        )
                    }
                    requestPosition()
                })
                .catch(() => requestPosition())
        } else {
            requestPosition()
        }
    }

    formatCoords(lat, lon) {
        const precision = Number.isFinite(this.precisionValue) ? this.precisionValue : null
        const latStr = precision == null ? String(lat) : lat.toFixed(precision)
        const lonStr = precision == null ? String(lon) : lon.toFixed(precision)

        return this.formatValue === "comma_space" ? `${latStr}, ${lonStr}` : `${latStr},${lonStr}`
    }
}
